import * as React from 'react'
import styled from 'styled-components'
import {Separator} from '../elements/Separator'
import {theme} from '../theme'
import {Button} from '../elements/Button'
import {Dropdown} from '../elements/Dropdown'
import {TextArea} from '../elements/TextArea'
import {CandidateStore} from '../stores/CandidateStore'
import {ICandidate, ICandidateFormBirthDay, IImageData} from '../types/candidate'
import CandidateForm from '../components/CandidateForm/CandidateForm'
import example_image from '../../images/example_image.png'
import {ImageModal} from '../components/ImageModal'
import {createCandidateElectionData, validDate} from '../utils'
import {RouteComponentProps} from 'react-router'
import {PartyStore} from '../stores/PartyStore'
import {inject, observer} from 'mobx-react'
import {Stores} from '../stores'
import {QuestionStore} from '../stores/QuestionStore'
import {computed} from 'mobx'
import {imagePrefix} from '../stores/config'
import moment from 'moment'
import {NotificationStore} from '../stores/NotificationStore'
import {AuthStore} from '../stores/AuthStore'
import ElectionSelection from '../components/ElectionSelection/ElectionSelection'
import CandidateData from '../components/CandidateData/CandidateData'

interface IProps extends RouteComponentProps<{electionId: string; candidateId: string}> {
  authStore: AuthStore
  candidateStore: CandidateStore
  partyStore: PartyStore
  questionStore: QuestionStore
  notificationStore: NotificationStore
}

interface IState {
  candidate: ICandidate
  birthday: ICandidateFormBirthDay
  imageModal: boolean
  src?: string
  crop: Pick<IImageData, 'x' | 'y' | 'width' | 'aspect'>
  croppedImageUrl?: any
  image?: Blob
  imageUploadIsLoading: boolean
  uploadedImgDimensions: {
    height: number
    width: number
  }
}

@inject((stores: Stores) => ({
  candidateStore: stores.candidateStore,
  partyStore: stores.partyStore,
  questionStore: stores.questionStore,
  notificationStore: stores.notificationStore,
  authStore: stores.authStore
}))
@observer
export class CandidateDetailsPage extends React.Component<IProps, IState> {
  private file: HTMLInputElement | null = null
  private imageRef: HTMLElement | null = null
  private fileUrl: string | null = null
  private cropInPixels?: Pick<IImageData, 'x' | 'y' | 'width' | 'aspect'> = undefined

  public readonly state: IState = {
    candidate: this.props.candidateStore.candidate!,
    birthday: {
      day: '',
      month: '',
      year: ''
    },
    imageModal: false,
    crop: {
      aspect: 1,
      width: 50,
      x: 1,
      y: 1
    },
    uploadedImgDimensions: {
      height: 0,
      width: 0
    },
    imageUploadIsLoading: false
  }

  @computed
  get candidateId() {
    return Number(this.props.match.params.candidateId)
  }

  private uploadImage = () => {
    this.file!.click()
  }

  componentDidMount() {
    if (this.props.candidateStore.candidate!.birthday) {
      const date = new Date(this.props.candidateStore.candidate!.birthday)
      this.setState({
        birthday: {
          day: date.getDate().toString(),
          month: (date.getMonth() + 1).toString(),
          year: date.getFullYear().toString()
        }
      })
    }

    const partyId = sessionStorage.getItem('party_id')

    if (partyId) {
      this.handleOnChange(Number(partyId), 'party_id')
    }
  }

  private onInputChange = (event: any) => {
    const file = event.target.files[0]
    if (file.size > 4 * 1024 * 1024) {
      this.props.notificationStore!.showNotification({message: 'Ladattu kuva on liian suuri. Maksimikoko on 4MB', type: 'error'})
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const img: HTMLImageElement = new Image()
      img.src = reader.result as string
      img.onload = () =>
        this.setState({
          src: reader.result as string,
          uploadedImgDimensions: {height: img.height, width: img.width}
        })
      this.setState({src: reader.result as string})
      this.toggleImageModal()
    }
    reader.readAsDataURL(file)
    this.setState({image: file})
    event.target.value = null
  }

  private handleOnChange = (val: any, key: string) => {
    if (key === 'election_data') {
      const election_data = Object.keys(val).reduce((acc, key) => {
        if (Object.values(val[key]).length) {
          acc[key] = {
            ...val[key],
            constituency_id: val[key].constituency_id || val[key].id,
            party_id: val[key].party_id || this.state.candidate.party_id
          }
        }
        return acc
      }, {})
      this.setState({candidate: {...this.state.candidate, election_data}})
    } else {
      this.setState({candidate: {...this.state.candidate, [key]: val || null}})
    }
  }

  private saveDetails = async () => {
    const {constituencyName = '', election_id, ...candidate} = this.state.candidate
    this.props.candidateStore.setConstituencyName(constituencyName)
    const election_data = createCandidateElectionData(this.state.candidate)

    await this.props.candidateStore.updateCandidate(
      {
        ...candidate,
        election_data,
        given_name: candidate.given_name || candidate.first_name,
        birthday: moment(validDate(this.state.candidate.birthday)!).format()
      },
      election_id
    )
  }

  private continueToAnswerPage = () => {
    const firstQuestion = this.props.questionStore.questions?.[0]?.id
    const candidateId = this.props.candidateStore.candidate?.tunnus_id || this.props.candidateStore.candidate?.id
    this.props.history.push(`/ehdokas/${candidateId}/kysymys/${firstQuestion}`)
  }

  private saveCroppedPhoto = async () => {
    this.setState({imageUploadIsLoading: true})
    const {candidate, image, uploadedImgDimensions} = this.state
    let width = this.cropInPixels!.width!

    if (uploadedImgDimensions.width > uploadedImgDimensions.height) {
      width = width >= uploadedImgDimensions.height ? uploadedImgDimensions.height - 1 : width
    } else {
      width = width >= uploadedImgDimensions.width ? uploadedImgDimensions.width - 1 : width
    }

    const data: IImageData = {...this.cropInPixels!, width, image: image!}

    await this.props.candidateStore.uploadPhoto(candidate, data)
    this.setState({candidate: {...this.state.candidate, image: this.props.candidateStore.candidate!.image}})
    this.toggleImageModal()
    this.setState({imageUploadIsLoading: false})
  }

  private toggleImageModal = () => {
    this.cropInPixels = undefined
    this.setState({imageModal: !this.state.imageModal, croppedImageUrl: undefined})
  }

  onImageLoaded = (image: any) => {
    this.imageRef = image
    this.setState({crop: {aspect: 1, x: 1, y: 1, width: 50}})
  }

  private onCropChange = (crop: any) => {
    // Don't allow the crop width (height is automatically equal to the width) to be 0, the user must crop a valid square
    if (crop.width && crop.width > 0) {
      this.setState({crop})
    }
  }

  onCropComplete = (crop: any, pixelCrop: any) => {
    this.makeClientCrop(crop, pixelCrop)
  }

  async makeClientCrop(crop: any, pixelCrop: any) {
    if (this.imageRef) {
      const croppedImageUrl = await this.getCroppedImg(this.imageRef, pixelCrop, 'newFile.jpeg')
      this.setState({croppedImageUrl})
    }
  }

  getCroppedImg(image: any, pixelCrop: any, fileName: string) {
    const canvas = document.createElement('canvas')
    canvas.width = pixelCrop.width || 1
    canvas.height = pixelCrop.height || 1
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height)

    this.cropInPixels = {
      width: pixelCrop.width || 1,
      x: pixelCrop.x,
      y: pixelCrop.y,
      aspect: 1
    }

    return new Promise(resolve => {
      canvas.toBlob((blob: any) => {
        blob.name = fileName
        window.URL.revokeObjectURL(this.fileUrl!)
        this.fileUrl = window.URL.createObjectURL(blob)
        resolve(this.fileUrl)
      }, 'image/jpeg')
    })
  }

  cancelCropping = () => {
    this.setState({src: undefined, croppedImageUrl: undefined})
    this.toggleImageModal()
  }

  public render() {
    const {candidate, src, croppedImageUrl, crop, birthday, imageUploadIsLoading} = this.state

    return (
      <Content>
        <ImageModal
          isOpen={this.state.imageModal}
          onSave={this.saveCroppedPhoto}
          onClose={this.cancelCropping}
          croppedImageUrl={croppedImageUrl}
          src={src}
          crop={crop}
          onImageLoaded={this.onImageLoaded}
          onCropChange={this.onCropChange}
          onCropComplete={this.onCropComplete}
          imageUploadIsLoading={imageUploadIsLoading}
          uploadIsDisabled={!this.cropInPixels}
        />
        <BoldSpan>Tervetuloa Iltalehden vaalikoneeseen!</BoldSpan>
        <br />
        <br />
        Ennen vastaamista täytäthän omat tietosi.{' '}
        <RedLink href="https://www.iltalehti.fi/" target="_blank">
          Iltalehti.fi
        </RedLink>{' '}
        tavoittaa joka viikko lähes kolme miljoonaa suomalaista. Iltalehden lisäksi tämä vaalikone on käytössä Uuden Suomen, Kauppalehden,
        Talouselämän, Turun Sanomien, Ilkka-Pohjalaisen, Keskipohjanmaan, Kainuun Sanomien, Kalevan sekä Lapin Kansan verkkosivuilla.
        <br />
        <br />
        Vaalikone aukeaa yleisölle perjantaina 14. maaliskuuta, kun ehdokasasettelu on vahvistettu ja ehdokasnumerot annettu. Ehdokkaat
        voivat kuitenkin täyttää konetta perjantaihin 21. maaliskuuta asti, minkä jälkeen ehdokaskäyttöliittymä sulkeutuu.
        <br />
        <br />
        Mikäli ehdokaskäyttöliittymän käytössä ilmenee ongelmia, voitte olla yhteydessä ehdokastukeemme osoitteessa{' '}
        <RedLink href="mailto: palaute.vaalikone@almamedia.fi">palaute.vaalikone@almamedia.fi</RedLink>. Vastaamme sähköposteihin
        viimeistään seuraavana arkipäivänä ja viikonloppuna tulleisiin yhteydenottoihin maanantaisin.
        <p>* Pakolliset tiedot</p>
        <CustomSeparator />
        <ElectionSelection handleOnChange={this.handleOnChange} candidate={candidate} />
        <CandidateData candidate={candidate} handleOnChange={this.handleOnChange} />
        <ImageContainer>
          <ImageUpload>
            <p>* Ehdokaskuva</p>
            <DefaultInput type={'file'} ref={(ref: any) => (this.file = ref)} id={'file'} name={'file'} onChange={this.onInputChange} />
            <ImageButtonAndInfoDiv>
              <Button onClick={this.uploadImage} height="40px" fontSize="18px">
                Lataa kuva
              </Button>
              {candidate.image && <SelectedImg>Kuva ladattu</SelectedImg>}
            </ImageButtonAndInfoDiv>
            <ImageInformation>
              Kuvan maksimikoko on 4MB, sallitut tiedostomuodot ovat .jpg, .jpeg ja .png. Varmista, että kuvan nimessä ei ole välilyöntejä.
            </ImageInformation>
          </ImageUpload>
          <ImageDiv>
            <img src={candidate.image ? `${imagePrefix}/${candidate.image}` : example_image} />
            <span>
              Vaalikoneen ehdokaskuvat ovat pyöreämuotoisia, joten ladattavan kuvan tulee olla sellainen, joka sopii oheisen kuvan mukaiseen
              muotoon. Kasvot tulee olla kuvassa keskellä.
            </span>
          </ImageDiv>
        </ImageContainer>
        <CustomSeparator />
        <CandidateForm
          handleOnChange={this.handleOnChange}
          candidate={candidate}
          birthday={birthday}
          saveDetails={this.saveDetails}
          continueToAnswerPage={this.continueToAnswerPage}
        />
      </Content>
    )
  }
}

const BoldSpan = styled.span`
  font-weight: ${theme.fontWeight.bold};
`

const ImageUpload = styled.div`
  padding-bottom: 12px;
`

const SelectedImg = styled.p`
  font-size: ${theme.fontSize.small};
`

const CustomSeparator = styled(Separator)`
  width: calc(100% + 16px);
  margin: 20px -8px 20px -8px;
`

// hides default file input field visually but keeps it visible for browser
const DefaultInput = styled.input`
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
`

const Content = styled.div`
  padding: 2.5rem 3.5rem 2rem 3.5rem;
  font-weight: 500;

  ${TextArea} {
    margin-bottom: 1.6rem;
  }
  ${Dropdown} {
    margin-bottom: 1.6rem;
  }
  .party-independent-checkbox {
    margin-top: 1rem;
    margin-left: 80px;
    span {
      border-radius: 0px;
    }
  }
  @media screen and (max-width: ${theme.breakPoints.mobileLandscape}px) {
    padding: 2rem 4rem;
  }
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    padding: ${theme.layout.mobileContentPadding};
    font-size: 1.7rem;
  }
`

const ImageContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  p {
    margin: 0.8rem 0;
  }
`

const ImageDiv = styled.div`
  display: flex;
  align-items: center;
  max-width: 400px;
  span {
    font-size: ${theme.fontSize.small};
    padding-right: 0.5rem;
  }
  img {
    height: 12rem;
    padding: 0 1rem;
    border-radius: 50%;
    flex-shrink: 0;
  }
`
const ImageInformation = styled.p`
  font-size: ${theme.fontSize.small};
  width: 280px;
`

const ImageButtonAndInfoDiv = styled.div`
  display: flex;
  justify-content: space-between;
  width: 215px;
`

const RedLink = styled.a`
  color: ${theme.color.red};
  text-decoration: none;
`

{
  /* For ekv
        <Checkbox
          className={'party-independent-checkbox'}
          label={'Olen sitoutumaton'}
          checked={candidate.independent || false}
          onChange={() => this.handleOnChange(!(candidate.independent || false), 'independent')}
        /> 
 */
}
